import type { ApiChain, ApiDerivationSpec, ApiNetwork, ApiToken, ApiTokenWithPrice, UTXOChain } from '../api/types';

import {
  ARBITRUM,
  ARBITRUM_USDC_MAINNET,
  ARC,
  AVALANCHE,
  AVALANCHE_USDT_MAINNET,
  BASE,
  BASE_USDC_MAINNET,
  BASE_USDT_MAINNET,
  BITCOIN,
  BITCOINCASH,
  BNB,
  BSC_USDT_MAINNET,
  DEBUG,
  DOGECOIN,
  ETH,
  ETH_USDC_MAINNET,
  ETH_USDT_MAINNET,
  HYPERLIQUID,
  HYPERLIQUID_USDC_MAINNET,
  IS_GRAM_WALLET,
  LITECOIN,
  MONAD,
  MYCOIN_MAINNET,
  MYCOIN_TESTNET,
  OPTIMISM,
  POLYGON,
  ROBINHOOD,
  SOLANA,
  SOLANA_USDC_MAINNET,
  SOLANA_USDT_MAINNET,
  TON_TSUSDE,
  TON_USDE,
  TON_USDT_MAINNET,
  TON_USDT_TESTNET,
  TONCOIN,
  TONSTAKERS_JETTON,
  TRC20_USDT_MAINNET,
  TRC20_USDT_TESTNET,
  TRX,
  ZCASH,
} from '../config';
import { EVM_DERIVATION_PATHS, EVM_FEE_CHECK_ADDRESS } from '../api/chains/evm/constants';
import { SOLANA_DERIVATION_PATHS } from '../api/chains/solana/constants';
import { SOLANA_DERIVATION_SPEC, SOLANA_DERIVATION_VERSION } from '../api/chains/solana/derivationConstants';
import { TON_BIP39_PATH } from '../api/chains/ton/derivationConstants';
import { TRON_BIP39_PATH } from '../api/chains/tron/constants';
import { getDefaultUtxoDerivationPath, UTXO_COIN_TYPES } from '../api/chains/utxo/constants';
import formatTonTransferUrl from './ton/formatTransferUrl';
import { buildCollectionByKey, compact } from './iteratees';
import withCache from './withCache';

export type ExplorerLink = {
  url: string;
  param: string;
} | string;

export interface BaseExplorerConfig {
  id: string;
  name: string;
  baseUrl: Record<ApiNetwork, ExplorerLink>;
}
export interface ExplorerConfig extends BaseExplorerConfig {
  /** Use `{base}` as the base URL placeholder and `{address}` as the wallet address placeholder */
  address: string;
  /** Use `{base}` as the base URL placeholder and `{address}` as the token address placeholder */
  token: string;
  /** Use `{base}` as the base URL placeholder and `{hash}` as the transaction hash placeholder */
  transaction: string;
  /** Use `{base}` as the base URL placeholder and `{address}` as the NFT address placeholder */
  nft?: string;
  /** Use `{base}` as the base URL placeholder and `{address}` as the NFT collection address placeholder */
  nftCollection?: string;
  doConvertHashFromBase64: boolean;
  /** Whether the transaction page also finds a TON trace by the hash of its external message */
  isMessageHashSupported?: boolean;
}

export interface MarketplaceConfig extends BaseExplorerConfig {
  nft: string;
  /** Use `{base}` as the base URL placeholder and `{address}` as the NFT collection address placeholder */
  nftCollection?: string;
}

/**
 * Describes the chain features that distinguish it from other chains in the multichain-polymorphic parts of the code.
 */
export interface ChainConfig {
  /** The blockchain title to show in the UI */
  title: string;
  /** The standard of the chain, e.g. `ethereum` for EVM chains */
  chainStandard?: ApiChain;
  /** Whether the chain supports domain names that resolve to regular addresses */
  isDnsSupported: boolean;
  /** Whether My Wallet supports purchasing crypto in that blockchain with a bank card in Russia */
  canBuyWithCardInRussia: boolean;
  /** Whether the chain is supported by the on-ramp widget (Moonpay outside RU, Avanchange in RU) */
  isOnRampSupported: boolean;
  /** Whether the chain is supported by the off-ramp widget (Moonpay) */
  isOffRampSupported: boolean;
  /** Whether the chain supports onchain swaps (DEX) */
  isOnchainSwapSupported: boolean;
  /** Whether onchain swaps can be estimated from the buy amount */
  canSwapByBuyAmount?: boolean;
  /** Whether the chain supports sending asset transfers with a comment */
  isTransferPayloadSupported: boolean;
  /** Whether the chain supports comment encrypting */
  isEncryptedCommentSupported: boolean;
  /** Whether the chain supports sending the full balance of the native token (the fee is taken from the sent amount) */
  canTransferFullNativeBalance: boolean;
  /** Whether SDK balances include pending local transfer debits until the observed value changes */
  shouldUsePendingBalances?: boolean;
  /** Whether Ledger support is implemented for this chain */
  isLedgerSupported: boolean;
  /** Whether the chain supports multiWallet (e.g. Solana derivations or TON versions) */
  isSubwalletsSupported: boolean;
  /** The default derivation path for the chain */
  defaultDerivationPath?: string;
  /**
   * Funded derivation path variants at the same index become separate accounts on seed import.
   * Extra format accounts keep that chain's wallet and derive other chains at a reserved index offset.
   */
  doesImportAddressFormatsSeparately?: boolean;
  /** Regular expression for wallet and contract addresses in the chain */
  addressRegex: RegExp;
  /** The same regular expression but matching any prefix of a valid address */
  addressPrefixRegex: RegExp;
  /** The native token of the chain, i.e. the token that pays the fees */
  nativeToken: ApiToken;
  /** Token addresses whose balances represent foreign staking positions */
  foreignStakingTokenAddresses?: string[];
  /** Whether our own backend socket (src/api/common/backendSocket.ts) supports this chain */
  doesBackendSocketSupport: boolean;
  /** Whether the SDK allows to import tokens by address */
  canImportTokens: boolean;
  /** If `true`, the Send form UI will show a scam warning if the wallet has tokens but not enough gas to sent them */
  shouldShowScamWarningIfNotEnoughGas: boolean;
  /** Whether our own backend supports push notifications for addresses in this chain */
  doesSupportPushNotifications: boolean;
  /** A random but valid address for checking transfer fees */
  feeCheckAddress: string;
  /** The slug of the USDT token in this chain, if it has USDT */
  usdtSlug: Record<ApiNetwork, string | undefined>;
  usdcSlug?: Record<ApiNetwork, string | undefined>;
  /** The token slugs of this chain added to new accounts by default. */
  defaultEnabledSlugs: Record<ApiNetwork, string[]>;
  /** The token slugs of this chain supported by the crosschain (CEX) swap mechanism. */
  crosschainSwapSlugs: string[];
  /**
   * The tokens to fill the token cache until it's loaded from the backend.
   * Should include the tokens from the above lists, and the staking tokens.
   */
  tokenInfo: (ApiToken & Partial<ApiTokenWithPrice>)[];
  /**
   * Configuration of available explorers for the chain.
   * The configuration does not contain data for NFT addresses, they must be configured separately.
   */
  explorers: ExplorerConfig[];

  /**
   * Configuration of available NFT marketplaces on chain.
   * Implements structure of config for explorers, but NFT-related fields only.
   * Empty, if NFTs are not supported
   */
  marketplaces: MarketplaceConfig[];
  /** Whether the chain supports NFTs */
  isNftSupported: boolean;
  /** The SDK splits one NFT submission into wallet-sized transactions using one authorization */
  canSubmitMultipleNftTransactions?: boolean;
  /** Max number of NFTs to request per pagination batch (for NFT-supporting chains) */
  nftBatchLimit?: number;
  /** Pause in ms between NFT pagination batches (for NFT-supporting chains) */
  nftBatchPauseMs?: number;
  /** Whether the chain supports net worth details */
  isNetWorthSupported: boolean;
  /** Brand color used to represent the chain in charts and other multichain visualizations */
  displayColor: string;
  /** Builds a link to transfer assets in this chain. If not set, the chain won't have the Deposit Link modal. */
  formatTransferUrl?(address: string, amount?: bigint, text?: string, jettonAddress?: string): string;

  /**
   * Declarative derivation spec used by the Enclave to derive a public wallet for this chain
   * from a stored BIP39 secret. Bumping `version` marks all stored wallets as in need of
   * re-derivation and triggers the chain-upgrade executor on next authorized entry.
   * Only chains that use the shared chain-upgrade pipeline set this field.
   */
  derivation?: {
    spec: ApiDerivationSpec;
    version: number;
  };
}

// Address-matching precedence order (NOT the display order — see `CHAIN_DISPLAY_ORDER` below).
// A pasted address is matched against chains in this order and the first match wins, so the chain with the more
// specific address regex must come first: e.g. a TRON address also matches Solana's regex, so `tron` must precede
// `solana`; and all EVM chains share the same regex, so the first EVM chain (`ethereum`) is the default match.
export const CHAIN_ORDER: ApiChain[] = [
  'ton',
  'tron',
  'zcash',
  'bitcoin',
  'litecoin',
  'dogecoin',
  'bitcoincash',
  'solana',
  'ethereum',
  'base',
  'bnb',
  'polygon',
  'arbitrum',
  'optimism',
  'monad',
  'avalanche',
  'hyperliquid',
  'arc',
  'robinhood',
];

// Display order for chains everywhere in the UI. Independent of `CHAIN_ORDER`,
// which is constrained by address-matching correctness.
// Must contain the same chains as `CHAIN_ORDER`.
export const CHAIN_DISPLAY_ORDER: ApiChain[] = [
  'bitcoin',
  'ethereum',
  'solana',
  'hyperliquid',
  'ton',
  'tron',
  'bnb',
  'base',
  'arc',
  'robinhood',
  'monad',
  'arbitrum',
  'polygon',
  'avalanche',
  'optimism',
  'zcash',
  'dogecoin',
  'litecoin',
  'bitcoincash',
];

const CHAIN_CONFIG: Record<ApiChain, ChainConfig> = {
  ton: {
    title: 'TON',
    isDnsSupported: true,
    canBuyWithCardInRussia: true,
    isOnRampSupported: true,
    isOffRampSupported: true,
    isOnchainSwapSupported: true,
    canSwapByBuyAmount: true,
    isTransferPayloadSupported: true,
    isEncryptedCommentSupported: true,
    canTransferFullNativeBalance: true,
    isLedgerSupported: true,
    isSubwalletsSupported: true,
    defaultDerivationPath: TON_BIP39_PATH,
    isNftSupported: true,
    canSubmitMultipleNftTransactions: true,
    addressRegex: /^([-\w_]{48}|0:[\da-h]{64})$/i,
    addressPrefixRegex: /^([-\w_]{1,48}|0:[\da-h]{0,64})$/i,
    nativeToken: TONCOIN,
    foreignStakingTokenAddresses: [TONSTAKERS_JETTON],
    displayColor: '#2C92F0',
    doesBackendSocketSupport: true,
    canImportTokens: true,
    shouldShowScamWarningIfNotEnoughGas: false,
    doesSupportPushNotifications: true,
    feeCheckAddress: 'UQBE5NzPPnfb6KAy7Rba2yQiuUnihrfcFw96T-p5JtZjAl_c',
    usdtSlug: {
      mainnet: TON_USDT_MAINNET.slug,
      testnet: TON_USDT_TESTNET.slug,
    },
    defaultEnabledSlugs: {
      mainnet: [TONCOIN.slug],
      testnet: [TONCOIN.slug],
    },
    crosschainSwapSlugs: [TONCOIN.slug, TON_USDT_MAINNET.slug],
    tokenInfo: [
      TONCOIN,
      TON_USDT_MAINNET,
      TON_USDT_TESTNET,
      MYCOIN_MAINNET,
      MYCOIN_TESTNET,
      TON_USDE,
      TON_TSUSDE,
    ],
    explorers: [
      {
        id: 'tonscan',
        name: 'Tonscan',
        baseUrl: {
          mainnet: 'https://tonscan.org/',
          testnet: 'https://testnet.tonscan.org/',
        },
        address: '{base}address/{address}',
        token: '{base}jetton/{address}',
        transaction: '{base}tx/{hash}',
        nft: '{base}nft/{address}',
        nftCollection: '{base}collection/{address}',
        doConvertHashFromBase64: true,
        isMessageHashSupported: true,
      },
      {
        id: 'tonviewer',
        name: 'Tonviewer',
        baseUrl: {
          mainnet: 'https://tonviewer.com/',
          testnet: 'https://testnet.tonviewer.com/',
        },
        address: '{base}{address}?address',
        token: '{base}{address}?jetton',
        transaction: '{base}transaction/{hash}',
        nft: '{base}{address}?nft',
        nftCollection: '{base}{address}?collection',
        doConvertHashFromBase64: true,
        isMessageHashSupported: true,
      },
      {
        id: 'actonscan',
        name: 'Actonscan',
        // Without `network`, the site opens the network the user viewed there last
        baseUrl: {
          mainnet: {
            url: 'https://actonscan.com/',
            param: '?network=mainnet',
          },
          testnet: {
            url: 'https://actonscan.com/',
            param: '?network=testnet',
          },
        },
        address: '{base}address/{address}',
        token: '{base}address/{address}',
        transaction: '{base}tx/{hash}',
        nft: '{base}address/{address}',
        nftCollection: '{base}address/{address}',
        doConvertHashFromBase64: true,
      },
    ],
    marketplaces: [{
      id: 'getgems',
      name: 'Getgems',
      baseUrl: {
        mainnet: 'https://getgems.io/',
        testnet: 'https://testnet.getgems.io/',
      },
      nft: '{base}nft/{address}',
      nftCollection: '{base}collection/{address}',
    }],
    nftBatchLimit: 500,
    nftBatchPauseMs: 1000,
    isNetWorthSupported: true,
    formatTransferUrl: formatTonTransferUrl,
  },
  tron: {
    title: 'TRON',
    isDnsSupported: false,
    canBuyWithCardInRussia: false,
    isOnRampSupported: true,
    isOffRampSupported: true,
    isOnchainSwapSupported: false,
    isTransferPayloadSupported: false,
    isEncryptedCommentSupported: false,
    canTransferFullNativeBalance: false,
    isLedgerSupported: false,
    isSubwalletsSupported: true,
    defaultDerivationPath: TRON_BIP39_PATH,
    isNftSupported: false,
    addressRegex: /^T[1-9A-HJ-NP-Za-km-z]{33}$/,
    addressPrefixRegex: /^T[1-9A-HJ-NP-Za-km-z]{0,33}$/,
    nativeToken: TRX,
    displayColor: '#E65850',
    doesBackendSocketSupport: true,
    canImportTokens: false,
    shouldShowScamWarningIfNotEnoughGas: true,
    doesSupportPushNotifications: false,
    feeCheckAddress: 'TW2LXSebZ7Br1zHaiA2W1zRojDkDwjGmpw',
    usdtSlug: {
      mainnet: TRC20_USDT_MAINNET.slug,
      testnet: TRC20_USDT_TESTNET.slug,
    },
    defaultEnabledSlugs: {
      mainnet: [TRX.slug],
      testnet: [TRX.slug],
    },
    crosschainSwapSlugs: [TRX.slug, TRC20_USDT_MAINNET.slug],
    tokenInfo: [
      TRX,
      TRC20_USDT_MAINNET,
      TRC20_USDT_TESTNET,
    ],
    explorers: [
      {
        id: 'tronscan',
        name: 'Tronscan',
        baseUrl: {
          mainnet: 'https://tronscan.org/#/',
          testnet: 'https://shasta.tronscan.org/#/',
        },
        address: '{base}address/{address}',
        token: '{base}token20/{address}',
        transaction: '{base}transaction/{hash}',
        doConvertHashFromBase64: false,
      },
    ],
    marketplaces: [],
    isNetWorthSupported: false,
  },
  solana: {
    title: 'Solana',
    isDnsSupported: false,
    canBuyWithCardInRussia: false,
    isOnRampSupported: true,
    isOffRampSupported: true,
    isOnchainSwapSupported: true,
    canSwapByBuyAmount: false,
    isTransferPayloadSupported: true,
    isEncryptedCommentSupported: false,
    canTransferFullNativeBalance: false,
    isLedgerSupported: false,
    isSubwalletsSupported: true,
    defaultDerivationPath: SOLANA_DERIVATION_PATHS.phantom,
    isNftSupported: true,
    addressRegex: /^[1-9A-HJ-NP-Za-km-z]{32,44}$/,
    addressPrefixRegex: /^[1-9A-HJ-NP-Za-km-z]{0,44}$/,
    nativeToken: SOLANA,
    displayColor: '#864BFF',
    doesBackendSocketSupport: false,
    canImportTokens: false,
    shouldShowScamWarningIfNotEnoughGas: false,
    feeCheckAddress: '35YT7tt9edJbroEKaC3T3XY4cLNWKtVzmyTEfW8LHPEA',
    usdtSlug: {
      mainnet: SOLANA_USDT_MAINNET.slug,
      testnet: undefined,
    },
    usdcSlug: {
      mainnet: SOLANA_USDC_MAINNET.slug,
      testnet: undefined,
    },
    defaultEnabledSlugs: {
      mainnet: [SOLANA.slug],
      testnet: [SOLANA.slug],
    },
    crosschainSwapSlugs: [SOLANA.slug, SOLANA_USDT_MAINNET.slug],
    tokenInfo: [
      SOLANA,
      SOLANA_USDT_MAINNET,
      SOLANA_USDC_MAINNET,
    ],
    explorers: [{
      id: 'solscan',
      name: 'Solscan',
      baseUrl: {
        mainnet: 'https://solscan.io/',
        testnet: {
          url: 'https://solscan.io/',
          param: '?cluster=devnet',
        },
      },
      address: '{base}account/{address}',
      token: '{base}token/{address}',
      transaction: '{base}tx/{hash}',
      nft: '{base}token/{address}',
      // Сollections on solana are grouping by master token address
      nftCollection: '{base}token/{address}',
      doConvertHashFromBase64: false,
    }],
    marketplaces: [{
      id: 'magicEden',
      name: 'Magic Eden',
      baseUrl: {
        mainnet: 'https://magiceden.io/',
        testnet: '', // No testnet support
      },
      nft: '{base}item-details/{address}',
    }],
    nftBatchLimit: 500,
    nftBatchPauseMs: 1000,
    isNetWorthSupported: false,
    doesSupportPushNotifications: false,
    derivation: {
      spec: SOLANA_DERIVATION_SPEC,
      version: SOLANA_DERIVATION_VERSION,
    },
  },
  ethereum: {
    title: 'Ethereum',
    chainStandard: 'ethereum',
    shouldUsePendingBalances: true,
    isDnsSupported: false,
    canBuyWithCardInRussia: false,
    isOnRampSupported: true,
    isOffRampSupported: true,
    isOnchainSwapSupported: true,
    canSwapByBuyAmount: true,
    isTransferPayloadSupported: false,
    isEncryptedCommentSupported: false,
    canTransferFullNativeBalance: false,
    isLedgerSupported: false,
    isSubwalletsSupported: true,
    defaultDerivationPath: EVM_DERIVATION_PATHS.default,
    addressRegex: /^0x[a-fA-F0-9]{40}$/,
    addressPrefixRegex: /^0x[a-fA-F0-9]{0,40}$/,
    nativeToken: ETH,
    displayColor: '#627EEA',
    doesBackendSocketSupport: false,
    canImportTokens: false,
    shouldShowScamWarningIfNotEnoughGas: false,
    feeCheckAddress: EVM_FEE_CHECK_ADDRESS,
    usdtSlug: {
      mainnet: ETH_USDT_MAINNET.slug,
      testnet: ETH_USDT_MAINNET.slug,
    },
    usdcSlug: {
      mainnet: ETH_USDC_MAINNET.slug,
      testnet: undefined,
    },
    defaultEnabledSlugs: {
      mainnet: [ETH.slug],
      testnet: [ETH.slug],
    },
    crosschainSwapSlugs: [ETH.slug, ETH_USDT_MAINNET.slug],
    tokenInfo: [
      ETH,
      ETH_USDT_MAINNET,
      ETH_USDC_MAINNET,
    ],
    explorers: [{
      id: 'etherscan',
      name: 'Etherscan',
      baseUrl: {
        mainnet: 'https://etherscan.io/',
        testnet: 'https://sepolia.etherscan.io/',
      },
      address: '{base}address/{address}',
      token: '{base}token/{address}',
      nft: '{base}nft/{address}',
      transaction: '{base}tx/{hash}',
      doConvertHashFromBase64: false,
    }],
    marketplaces: [{
      id: 'openSea',
      name: 'OpenSea',
      baseUrl: {
        mainnet: 'https://opensea.io/',
        testnet: '', // No testnet support
      },
      nft: '{base}item/{chain}/{address}',
    }],
    isNetWorthSupported: false,
    doesSupportPushNotifications: false,
    isNftSupported: true,
  },
  base: {
    title: 'Base',
    chainStandard: 'ethereum',
    shouldUsePendingBalances: true,
    isDnsSupported: false,
    canBuyWithCardInRussia: false,
    isOnRampSupported: true,
    isOffRampSupported: true,
    isOnchainSwapSupported: true,
    canSwapByBuyAmount: true,
    isTransferPayloadSupported: false,
    isEncryptedCommentSupported: false,
    canTransferFullNativeBalance: false,
    isLedgerSupported: false,
    isSubwalletsSupported: true,
    defaultDerivationPath: EVM_DERIVATION_PATHS.default,
    addressRegex: /^0x[a-fA-F0-9]{40}$/,
    addressPrefixRegex: /^0x[a-fA-F0-9]{0,40}$/,
    nativeToken: BASE,
    displayColor: '#0052FF',
    doesBackendSocketSupport: false,
    canImportTokens: false,
    shouldShowScamWarningIfNotEnoughGas: false,
    feeCheckAddress: EVM_FEE_CHECK_ADDRESS,
    usdtSlug: {
      mainnet: BASE_USDT_MAINNET.slug,
      testnet: BASE_USDT_MAINNET.slug,
    },
    usdcSlug: {
      mainnet: BASE_USDC_MAINNET.slug,
      testnet: undefined,
    },
    defaultEnabledSlugs: {
      mainnet: [BASE.slug],
      testnet: [BASE.slug],
    },
    crosschainSwapSlugs: [BASE.slug],
    tokenInfo: [BASE, BASE_USDT_MAINNET, BASE_USDC_MAINNET],
    explorers: [{
      id: 'basescan',
      name: 'BaseScan',
      baseUrl: {
        mainnet: 'https://basescan.org/',
        testnet: 'https://sepolia.basescan.org/',
      },
      address: '{base}address/{address}',
      token: '{base}token/{address}',
      nft: '{base}nft/{address}',
      transaction: '{base}tx/{hash}',
      doConvertHashFromBase64: false,
    }],
    marketplaces: [{
      id: 'openSea',
      name: 'OpenSea',
      baseUrl: {
        mainnet: 'https://opensea.io/',
        testnet: '', // No testnet support
      },
      nft: '{base}item/{chain}/{address}',
    }],
    isNetWorthSupported: false,
    doesSupportPushNotifications: false,
    isNftSupported: true,
  },
  bnb: {
    title: 'BNB',
    chainStandard: 'ethereum',
    shouldUsePendingBalances: true,
    isDnsSupported: false,
    canBuyWithCardInRussia: false,
    isOnRampSupported: true,
    isOffRampSupported: true,
    isOnchainSwapSupported: true,
    canSwapByBuyAmount: true,
    isTransferPayloadSupported: false,
    isEncryptedCommentSupported: false,
    canTransferFullNativeBalance: false,
    isLedgerSupported: false,
    isSubwalletsSupported: true,
    defaultDerivationPath: EVM_DERIVATION_PATHS.default,
    addressRegex: /^0x[a-fA-F0-9]{40}$/,
    addressPrefixRegex: /^0x[a-fA-F0-9]{0,40}$/,
    nativeToken: BNB,
    displayColor: '#F39D08',
    doesBackendSocketSupport: false,
    canImportTokens: false,
    shouldShowScamWarningIfNotEnoughGas: false,
    feeCheckAddress: EVM_FEE_CHECK_ADDRESS,
    usdtSlug: {
      mainnet: BSC_USDT_MAINNET.slug,
      testnet: BSC_USDT_MAINNET.slug,
    },
    defaultEnabledSlugs: {
      mainnet: [BNB.slug],
      testnet: [BNB.slug],
    },
    crosschainSwapSlugs: [BNB.slug],
    tokenInfo: [BNB, BSC_USDT_MAINNET],
    explorers: [{
      id: 'bsctrace',
      name: 'BSCTrace',
      baseUrl: {
        mainnet: 'https://bscscan.com/',
        testnet: 'https://testnet.bscscan.com/',
      },
      address: '{base}address/{address}',
      token: '{base}token/{address}',
      nft: '{base}nft/{address}',
      transaction: '{base}tx/{hash}',
      doConvertHashFromBase64: false,
    }],
    marketplaces: [{
      id: 'openSea',
      name: 'OpenSea',
      baseUrl: {
        mainnet: 'https://opensea.io/',
        testnet: '', // No testnet support
      },
      nft: '{base}item/{chain}/{address}',
    }],
    isNetWorthSupported: false,
    doesSupportPushNotifications: false,
    isNftSupported: true,
  },
  polygon: {
    title: 'Polygon',
    chainStandard: 'ethereum',
    shouldUsePendingBalances: true,
    isDnsSupported: false,
    canBuyWithCardInRussia: false,
    isOnRampSupported: true,
    isOffRampSupported: true,
    isOnchainSwapSupported: false,
    canSwapByBuyAmount: false,
    isTransferPayloadSupported: false,
    isEncryptedCommentSupported: false,
    canTransferFullNativeBalance: false,
    isLedgerSupported: false,
    isSubwalletsSupported: true,
    defaultDerivationPath: EVM_DERIVATION_PATHS.default,
    addressRegex: /^0x[a-fA-F0-9]{40}$/,
    addressPrefixRegex: /^0x[a-fA-F0-9]{0,40}$/,
    nativeToken: POLYGON,
    displayColor: '#8247E5',
    doesBackendSocketSupport: false,
    canImportTokens: false,
    shouldShowScamWarningIfNotEnoughGas: false,
    feeCheckAddress: EVM_FEE_CHECK_ADDRESS,
    usdtSlug: {
      mainnet: '',
      testnet: '',
    },
    defaultEnabledSlugs: {
      mainnet: [POLYGON.slug],
      testnet: [POLYGON.slug],
    },
    crosschainSwapSlugs: [POLYGON.slug],
    tokenInfo: [POLYGON],
    explorers: [{
      id: 'polygonscan',
      name: 'Polygonscan',
      baseUrl: {
        mainnet: 'https://polygonscan.com/',
        testnet: 'https://testnet.polygonscan.com/',
      },
      address: '{base}address/{address}',
      token: '{base}token/{address}',
      nft: '{base}nft/{address}',
      transaction: '{base}tx/{hash}',
      doConvertHashFromBase64: false,
    }],
    marketplaces: [{
      id: 'openSea',
      name: 'OpenSea',
      baseUrl: {
        mainnet: 'https://opensea.io/',
        testnet: '', // No testnet support
      },
      nft: '{base}item/{chain}/{address}',
    }],
    isNetWorthSupported: false,
    doesSupportPushNotifications: false,
    isNftSupported: true,
  },
  arbitrum: {
    title: 'Arbitrum',
    chainStandard: 'ethereum',
    shouldUsePendingBalances: true,
    isDnsSupported: false,
    canBuyWithCardInRussia: false,
    isOnRampSupported: true,
    isOffRampSupported: true,
    isOnchainSwapSupported: true,
    canSwapByBuyAmount: true,
    isTransferPayloadSupported: false,
    isEncryptedCommentSupported: false,
    canTransferFullNativeBalance: false,
    isLedgerSupported: false,
    isSubwalletsSupported: true,
    defaultDerivationPath: EVM_DERIVATION_PATHS.default,
    addressRegex: /^0x[a-fA-F0-9]{40}$/,
    addressPrefixRegex: /^0x[a-fA-F0-9]{0,40}$/,
    nativeToken: ARBITRUM,
    displayColor: '#28A0F0',
    doesBackendSocketSupport: false,
    canImportTokens: false,
    shouldShowScamWarningIfNotEnoughGas: false,
    feeCheckAddress: EVM_FEE_CHECK_ADDRESS,
    usdtSlug: {
      mainnet: '',
      testnet: '',
    },
    usdcSlug: {
      mainnet: ARBITRUM_USDC_MAINNET.slug,
      testnet: undefined,
    },
    defaultEnabledSlugs: {
      mainnet: [ARBITRUM.slug],
      testnet: [ARBITRUM.slug],
    },
    crosschainSwapSlugs: [ARBITRUM.slug],
    tokenInfo: [ARBITRUM, ARBITRUM_USDC_MAINNET],
    explorers: [{
      id: 'arbiscan',
      name: 'Arbiscan',
      baseUrl: {
        mainnet: 'https://arbiscan.io/',
        testnet: 'https://sepolia.arbiscan.io/',
      },
      address: '{base}address/{address}',
      token: '{base}token/{address}',
      nft: '{base}nft/{address}',
      transaction: '{base}tx/{hash}',
      doConvertHashFromBase64: false,
    }],
    marketplaces: [{
      id: 'openSea',
      name: 'OpenSea',
      baseUrl: {
        mainnet: 'https://opensea.io/',
        testnet: '', // No testnet support
      },
      nft: '{base}item/{chain}/{address}',
    }],
    isNetWorthSupported: false,
    doesSupportPushNotifications: false,
    isNftSupported: true,
  },
  optimism: {
    title: 'Optimism',
    chainStandard: 'ethereum',
    shouldUsePendingBalances: true,
    isDnsSupported: false,
    canBuyWithCardInRussia: false,
    isOnRampSupported: true,
    isOffRampSupported: false,
    isOnchainSwapSupported: true,
    canSwapByBuyAmount: true,
    isTransferPayloadSupported: false,
    isEncryptedCommentSupported: false,
    canTransferFullNativeBalance: false,
    isLedgerSupported: false,
    isSubwalletsSupported: true,
    defaultDerivationPath: EVM_DERIVATION_PATHS.default,
    addressRegex: /^0x[a-fA-F0-9]{40}$/,
    addressPrefixRegex: /^0x[a-fA-F0-9]{0,40}$/,
    nativeToken: OPTIMISM,
    displayColor: '#FF4F4F',
    doesBackendSocketSupport: false,
    canImportTokens: false,
    shouldShowScamWarningIfNotEnoughGas: false,
    feeCheckAddress: EVM_FEE_CHECK_ADDRESS,
    usdtSlug: {
      mainnet: '',
      testnet: '',
    },
    defaultEnabledSlugs: {
      mainnet: [OPTIMISM.slug],
      testnet: [OPTIMISM.slug],
    },
    crosschainSwapSlugs: [OPTIMISM.slug],
    tokenInfo: [OPTIMISM],
    explorers: [{
      id: 'optimismscan',
      name: 'Optimism Etherscan',
      baseUrl: {
        mainnet: 'https://optimistic.etherscan.io/',
        testnet: 'https://sepolia-optimism.etherscan.io/',
      },
      address: '{base}address/{address}',
      token: '{base}token/{address}',
      nft: '{base}nft/{address}',
      transaction: '{base}tx/{hash}',
      doConvertHashFromBase64: false,
    }],
    marketplaces: [{
      id: 'openSea',
      name: 'OpenSea',
      baseUrl: {
        mainnet: 'https://opensea.io/',
        testnet: '',
      },
      nft: '{base}item/{chain}/{address}',
    }],
    isNetWorthSupported: false,
    doesSupportPushNotifications: false,
    isNftSupported: true,
  },
  monad: {
    title: 'Monad',
    chainStandard: 'ethereum',
    shouldUsePendingBalances: true,
    isDnsSupported: false,
    canBuyWithCardInRussia: false,
    isOnRampSupported: false,
    isOffRampSupported: false,
    isOnchainSwapSupported: false,
    canSwapByBuyAmount: false,
    isTransferPayloadSupported: false,
    isEncryptedCommentSupported: false,
    canTransferFullNativeBalance: false,
    isLedgerSupported: false,
    isSubwalletsSupported: true,
    defaultDerivationPath: EVM_DERIVATION_PATHS.default,
    addressRegex: /^0x[a-fA-F0-9]{40}$/,
    addressPrefixRegex: /^0x[a-fA-F0-9]{0,40}$/,
    nativeToken: MONAD,
    displayColor: '#5F51AA',
    doesBackendSocketSupport: false,
    canImportTokens: false,
    shouldShowScamWarningIfNotEnoughGas: false,
    feeCheckAddress: EVM_FEE_CHECK_ADDRESS,
    usdtSlug: {
      mainnet: '',
      testnet: '',
    },
    defaultEnabledSlugs: {
      mainnet: [MONAD.slug],
      testnet: [MONAD.slug],
    },
    crosschainSwapSlugs: [MONAD.slug],
    tokenInfo: [MONAD],
    explorers: [{
      id: 'monadscan',
      name: 'Monadscan',
      baseUrl: {
        mainnet: 'https://monadscan.com/',
        testnet: 'https://testnet.monadscan.com/',
      },
      address: '{base}address/{address}',
      token: '{base}token/{address}',
      nft: '{base}nft/{address}',
      transaction: '{base}tx/{hash}',
      doConvertHashFromBase64: false,
    }],
    marketplaces: [{
      id: 'openSea',
      name: 'OpenSea',
      baseUrl: {
        mainnet: 'https://opensea.io/',
        testnet: '', // No testnet support
      },
      nft: '{base}item/{chain}/{address}',
    }],
    isNetWorthSupported: false,
    doesSupportPushNotifications: false,
    isNftSupported: false,
  },
  avalanche: {
    title: 'Avalanche',
    chainStandard: 'ethereum',
    shouldUsePendingBalances: true,
    isDnsSupported: false,
    canBuyWithCardInRussia: false,
    isOnRampSupported: true,
    isOffRampSupported: true,
    isOnchainSwapSupported: false,
    canSwapByBuyAmount: false,
    isTransferPayloadSupported: false,
    isEncryptedCommentSupported: false,
    canTransferFullNativeBalance: false,
    isLedgerSupported: false,
    isSubwalletsSupported: true,
    defaultDerivationPath: EVM_DERIVATION_PATHS.default,
    addressRegex: /^0x[a-fA-F0-9]{40}$/,
    addressPrefixRegex: /^0x[a-fA-F0-9]{0,40}$/,
    nativeToken: AVALANCHE,
    displayColor: '#A32F22',
    doesBackendSocketSupport: false,
    canImportTokens: false,
    shouldShowScamWarningIfNotEnoughGas: false,
    feeCheckAddress: EVM_FEE_CHECK_ADDRESS,
    usdtSlug: {
      mainnet: AVALANCHE_USDT_MAINNET.slug,
      testnet: AVALANCHE_USDT_MAINNET.slug,
    },
    defaultEnabledSlugs: {
      testnet: [AVALANCHE.slug],
      mainnet: [AVALANCHE.slug],
    },
    crosschainSwapSlugs: [AVALANCHE.slug],
    tokenInfo: [AVALANCHE, AVALANCHE_USDT_MAINNET],
    explorers: [{
      id: 'snowtrace',
      name: 'Snowtrace',
      baseUrl: {
        mainnet: 'https://snowtrace.io/',
        testnet: 'https://testnet.snowtrace.io/',
      },
      address: '{base}address/{address}',
      token: '{base}token/{address}',
      nft: '{base}nft/{address}',
      transaction: '{base}tx/{hash}',
      doConvertHashFromBase64: false,
    }],
    marketplaces: [{
      id: 'openSea',
      name: 'OpenSea',
      baseUrl: {
        mainnet: 'https://opensea.io/',
        testnet: '', // No testnet support
      },
      nft: '{base}item/{chain}/{address}',
    }],
    isNetWorthSupported: false,
    doesSupportPushNotifications: false,
    isNftSupported: false,
  },
  hyperliquid: {
    title: 'Hyperliquid',
    chainStandard: 'ethereum',
    shouldUsePendingBalances: true,
    isDnsSupported: false,
    canBuyWithCardInRussia: false,
    isOnRampSupported: true,
    isOffRampSupported: false,
    isOnchainSwapSupported: false,
    isTransferPayloadSupported: false,
    isEncryptedCommentSupported: false,
    canTransferFullNativeBalance: false,
    isLedgerSupported: false,
    isSubwalletsSupported: true,
    defaultDerivationPath: EVM_DERIVATION_PATHS.default,
    addressRegex: /^0x[a-fA-F0-9]{40}$/,
    addressPrefixRegex: /^0x[a-fA-F0-9]{0,40}$/,
    nativeToken: HYPERLIQUID,
    displayColor: '#00AF98',
    doesBackendSocketSupport: false,
    canImportTokens: false,
    shouldShowScamWarningIfNotEnoughGas: false,
    feeCheckAddress: EVM_FEE_CHECK_ADDRESS,
    usdtSlug: {
      mainnet: undefined,
      testnet: undefined,
    },
    usdcSlug: {
      mainnet: HYPERLIQUID_USDC_MAINNET.slug,
      testnet: undefined,
    },
    defaultEnabledSlugs: {
      mainnet: [HYPERLIQUID.slug],
      testnet: [HYPERLIQUID.slug],
    },
    crosschainSwapSlugs: [HYPERLIQUID.slug, HYPERLIQUID_USDC_MAINNET.slug],
    tokenInfo: [HYPERLIQUID, HYPERLIQUID_USDC_MAINNET],
    explorers: [{
      id: 'hyperevmscan',
      name: 'Hyperevmscan',
      baseUrl: {
        mainnet: 'https://hyperevmscan.io/',
        testnet: 'https://hyperevmscan.io/',
      },
      address: '{base}address/{address}',
      token: '{base}token/{address}',
      nft: '{base}nft/{address}',
      transaction: '{base}tx/{hash}',
      doConvertHashFromBase64: false,
    }],
    marketplaces: [{
      id: 'openSea',
      name: 'OpenSea',
      baseUrl: {
        mainnet: 'https://opensea.io/',
        testnet: '', // No testnet support
      },
      nft: '{base}item/{chain}/{address}',
    }],
    isNetWorthSupported: false,
    doesSupportPushNotifications: false,
    isNftSupported: false,
  },
  robinhood: {
    title: 'Robinhood',
    chainStandard: 'ethereum',
    shouldUsePendingBalances: true,
    isDnsSupported: false,
    canBuyWithCardInRussia: false,
    isOnRampSupported: true,
    isOffRampSupported: false,
    isOnchainSwapSupported: true,
    canSwapByBuyAmount: true,
    isTransferPayloadSupported: false,
    isEncryptedCommentSupported: false,
    canTransferFullNativeBalance: false,
    isLedgerSupported: false,
    isSubwalletsSupported: true,
    defaultDerivationPath: EVM_DERIVATION_PATHS.default,
    addressRegex: /^0x[a-fA-F0-9]{40}$/,
    addressPrefixRegex: /^0x[a-fA-F0-9]{0,40}$/,
    nativeToken: ROBINHOOD,
    displayColor: '#CCFF00',
    doesBackendSocketSupport: false,
    canImportTokens: false,
    shouldShowScamWarningIfNotEnoughGas: false,
    feeCheckAddress: EVM_FEE_CHECK_ADDRESS,
    usdtSlug: {
      mainnet: undefined,
      testnet: undefined,
    },
    defaultEnabledSlugs: {
      mainnet: [ROBINHOOD.slug],
      testnet: [ROBINHOOD.slug],
    },
    crosschainSwapSlugs: [ROBINHOOD.slug],
    tokenInfo: [ROBINHOOD],
    explorers: [{
      id: 'robinscan',
      name: 'Robinscan',
      baseUrl: {
        mainnet: 'https://robinscan.io/',
        testnet: 'https://robinscan.io/',
      },
      address: '{base}address/{address}',
      token: '{base}token/{address}',
      nft: '{base}nft/{address}',
      transaction: '{base}tx/{hash}',
      doConvertHashFromBase64: false,
    }],
    marketplaces: [{
      id: 'openSea',
      name: 'OpenSea',
      baseUrl: {
        mainnet: 'https://opensea.io/',
        testnet: '', // No testnet support
      },
      nft: '{base}item/{chain}/{address}',
    }],
    isNetWorthSupported: false,
    doesSupportPushNotifications: false,
    isNftSupported: false,
  },
  arc: {
    title: 'Arc',
    chainStandard: 'ethereum',
    shouldUsePendingBalances: true,
    isDnsSupported: false,
    canBuyWithCardInRussia: false,
    isOnRampSupported: true,
    isOffRampSupported: false,
    isOnchainSwapSupported: true,
    canSwapByBuyAmount: true,
    isTransferPayloadSupported: false,
    isEncryptedCommentSupported: false,
    canTransferFullNativeBalance: false,
    isLedgerSupported: false,
    isSubwalletsSupported: true,
    defaultDerivationPath: EVM_DERIVATION_PATHS.default,
    addressRegex: /^0x[a-fA-F0-9]{40}$/,
    addressPrefixRegex: /^0x[a-fA-F0-9]{0,40}$/,
    nativeToken: ARC,
    displayColor: '#708BB8',
    doesBackendSocketSupport: false,
    canImportTokens: false,
    shouldShowScamWarningIfNotEnoughGas: false,
    feeCheckAddress: EVM_FEE_CHECK_ADDRESS,
    usdtSlug: {
      mainnet: undefined,
      testnet: undefined,
    },
    defaultEnabledSlugs: {
      mainnet: [ARC.slug],
      testnet: [ARC.slug],
    },
    crosschainSwapSlugs: [ARC.slug],
    tokenInfo: [ARC],
    explorers: [{
      id: 'arcscan',
      name: 'Arcscan',
      baseUrl: {
        mainnet: 'https://arc-scan.org/',
        testnet: 'https://testnet.arc-scan.org/',
      },
      address: '{base}address/{address}',
      token: '{base}token/{address}',
      transaction: '{base}tx/{hash}',
      doConvertHashFromBase64: false,
    }],
    marketplaces: [{
      id: 'openSea',
      name: 'OpenSea',
      baseUrl: {
        mainnet: 'https://opensea.io/',
        testnet: '', // No testnet support
      },
      nft: '{base}item/{chain}/{address}',
    }],
    isNetWorthSupported: false,
    doesSupportPushNotifications: false,
    isNftSupported: false,
  },
  bitcoin: {
    title: 'Bitcoin',
    isDnsSupported: false,
    canBuyWithCardInRussia: false,
    isOnRampSupported: true,
    isOffRampSupported: true,
    isOnchainSwapSupported: false,
    isTransferPayloadSupported: false,
    isEncryptedCommentSupported: false,
    canTransferFullNativeBalance: true,
    isLedgerSupported: false,
    isSubwalletsSupported: true,
    defaultDerivationPath: getDefaultUtxoDerivationPath('bitcoin'),
    doesImportAddressFormatsSeparately: true,
    isNftSupported: false,
    addressRegex: /^(?:bc1|tb1)[a-z0-9]{25,62}$|^[13][a-km-zA-HJ-NP-Z1-9]{25,34}$/,
    addressPrefixRegex: /^(?:bc1|tb1)[a-z0-9]{0,62}$|^[13][a-km-zA-HJ-NP-Z1-9]{0,34}$/,
    nativeToken: BITCOIN,
    displayColor: '#F7931A',
    doesBackendSocketSupport: false,
    canImportTokens: false,
    shouldShowScamWarningIfNotEnoughGas: false,
    feeCheckAddress: 'bc1qar0srrr7xfkvy5l643lydnw9re59gtzzwf5mdq',
    usdtSlug: {
      mainnet: undefined,
      testnet: undefined,
    },
    doesSupportPushNotifications: false,
    defaultEnabledSlugs: {
      mainnet: [BITCOIN.slug],
      testnet: [BITCOIN.slug],
    },
    crosschainSwapSlugs: [BITCOIN.slug],
    tokenInfo: [BITCOIN],
    explorers: [{
      id: 'mempool',
      name: 'Mempool',
      baseUrl: {
        mainnet: 'https://mempool.space/',
        testnet: 'https://mempool.space/testnet/',
      },
      address: '{base}address/{address}',
      token: '{base}address/{address}',
      transaction: '{base}tx/{hash}',
      doConvertHashFromBase64: false,
    }],
    marketplaces: [],
    isNetWorthSupported: false,
  },
  litecoin: {
    title: 'Litecoin',
    isDnsSupported: false,
    canBuyWithCardInRussia: false,
    isOnRampSupported: true,
    isOffRampSupported: true,
    isOnchainSwapSupported: false,
    isTransferPayloadSupported: false,
    isEncryptedCommentSupported: false,
    canTransferFullNativeBalance: true,
    isLedgerSupported: false,
    isSubwalletsSupported: true,
    defaultDerivationPath: getDefaultUtxoDerivationPath('litecoin'),
    doesImportAddressFormatsSeparately: true,
    isNftSupported: false,
    addressRegex: /^(?:ltc1|tltc1)[a-z0-9]{25,62}$|^[LM2lm][a-km-zA-HJ-NP-Z1-9]{26,33}$/,
    addressPrefixRegex: /^(?:ltc1|tltc1)[a-z0-9]{0,62}$|^[LM2lm][a-km-zA-HJ-NP-Z1-9]{0,33}$/,
    nativeToken: LITECOIN,
    displayColor: '#345D9D',
    doesBackendSocketSupport: false,
    canImportTokens: false,
    shouldShowScamWarningIfNotEnoughGas: false,
    feeCheckAddress: 'ltc1qw508d6qejxtdg4y5r3zarvary0c5xw7kgmn4n9',
    usdtSlug: {
      mainnet: undefined,
      testnet: undefined,
    },
    doesSupportPushNotifications: false,
    defaultEnabledSlugs: {
      mainnet: [LITECOIN.slug],
      testnet: [LITECOIN.slug],
    },
    crosschainSwapSlugs: [LITECOIN.slug],
    tokenInfo: [LITECOIN],
    explorers: [{
      id: 'blockchair',
      name: 'Blockchair',
      baseUrl: {
        mainnet: 'https://blockchair.com/litecoin/',
        testnet: 'https://blockchair.com/litecoin/testnet/',
      },
      address: '{base}address/{address}',
      token: '{base}address/{address}',
      transaction: '{base}transaction/{hash}',
      doConvertHashFromBase64: false,
    }],
    marketplaces: [],
    isNetWorthSupported: false,
  },
  bitcoincash: {
    title: 'Bitcoin Cash',
    isDnsSupported: false,
    canBuyWithCardInRussia: false,
    isOnRampSupported: true,
    isOffRampSupported: true,
    isOnchainSwapSupported: false,
    isTransferPayloadSupported: false,
    isEncryptedCommentSupported: false,
    canTransferFullNativeBalance: true,
    isLedgerSupported: false,
    isSubwalletsSupported: true,
    defaultDerivationPath: getDefaultUtxoDerivationPath('bitcoincash'),
    doesImportAddressFormatsSeparately: true,
    isNftSupported: false,
    addressRegex: /^(?:bitcoincash:|bchtest:)?[qp][a-z0-9]{41}$|^[13][a-km-zA-HJ-NP-Z1-9]{25,34}$/i,
    addressPrefixRegex: /^(?:bitcoincash:|bchtest:)?[qp][a-z0-9]{0,41}$|^[13][a-km-zA-HJ-NP-Z1-9]{0,34}$/i,
    nativeToken: BITCOINCASH,
    displayColor: '#0AC18E',
    doesBackendSocketSupport: false,
    canImportTokens: false,
    shouldShowScamWarningIfNotEnoughGas: false,
    feeCheckAddress: 'qrny4xkwvwvsxertkd2nue70wmc5s98kru7m2q7vk6',
    usdtSlug: {
      mainnet: undefined,
      testnet: undefined,
    },
    doesSupportPushNotifications: false,
    defaultEnabledSlugs: {
      mainnet: [BITCOINCASH.slug],
      testnet: [BITCOINCASH.slug],
    },
    crosschainSwapSlugs: [BITCOINCASH.slug],
    tokenInfo: [BITCOINCASH],
    explorers: [{
      id: 'blockchair',
      name: 'Blockchair',
      baseUrl: {
        mainnet: 'https://blockchair.com/bitcoin-cash/',
        testnet: 'https://blockchair.com/bitcoin-cash/testnet/',
      },
      address: '{base}address/{address}',
      token: '{base}address/{address}',
      transaction: '{base}transaction/{hash}',
      doConvertHashFromBase64: false,
    }],
    marketplaces: [],
    isNetWorthSupported: false,
  },
  dogecoin: {
    title: 'Dogecoin',
    isDnsSupported: false,
    canBuyWithCardInRussia: false,
    isOnRampSupported: true,
    isOffRampSupported: true,
    isOnchainSwapSupported: false,
    isTransferPayloadSupported: false,
    isEncryptedCommentSupported: false,
    canTransferFullNativeBalance: true,
    isLedgerSupported: false,
    isSubwalletsSupported: true,
    defaultDerivationPath: getDefaultUtxoDerivationPath('dogecoin'),
    doesImportAddressFormatsSeparately: true,
    isNftSupported: false,
    addressRegex: /^D[5-9A-HJ-NP-U][1-9A-HJ-NP-Za-km-z]{32}$/,
    addressPrefixRegex: /^D[5-9A-HJ-NP-U][1-9A-HJ-NP-Za-km-z]{0,32}$/,
    nativeToken: DOGECOIN,
    displayColor: '#C2A633',
    doesBackendSocketSupport: false,
    canImportTokens: false,
    shouldShowScamWarningIfNotEnoughGas: false,
    feeCheckAddress: 'D596YFweJQuHY1BbjazZYmAbt8jJPbKehC',
    usdtSlug: {
      mainnet: undefined,
      testnet: undefined,
    },
    doesSupportPushNotifications: false,
    defaultEnabledSlugs: {
      mainnet: [DOGECOIN.slug],
      testnet: [DOGECOIN.slug],
    },
    crosschainSwapSlugs: [DOGECOIN.slug],
    tokenInfo: [DOGECOIN],
    explorers: [{
      id: 'blockchair',
      name: 'Blockchair',
      baseUrl: {
        mainnet: 'https://blockchair.com/dogecoin/',
        testnet: 'https://blockchair.com/dogecoin/testnet/',
      },
      address: '{base}address/{address}',
      token: '{base}address/{address}',
      transaction: '{base}transaction/{hash}',
      doConvertHashFromBase64: false,
    }],
    marketplaces: [],
    isNetWorthSupported: false,
  },
  zcash: {
    title: 'Zcash',
    isDnsSupported: false,
    canBuyWithCardInRussia: false,
    isOnRampSupported: false,
    isOffRampSupported: false,
    isOnchainSwapSupported: false,
    isTransferPayloadSupported: false,
    isEncryptedCommentSupported: false,
    canTransferFullNativeBalance: true,
    isLedgerSupported: false,
    isSubwalletsSupported: true,
    defaultDerivationPath: getDefaultUtxoDerivationPath('zcash'),
    doesImportAddressFormatsSeparately: true,
    isNftSupported: false,
    addressRegex: /^t[13][a-km-zA-HJ-NP-Z1-9]{33}$|^tm[a-km-zA-HJ-NP-Z1-9]{33}$|^t2[a-km-zA-HJ-NP-Z1-9]{33}$/,
    addressPrefixRegex: /^t[123m][a-km-zA-HJ-NP-Z1-9]{0,33}$/,
    nativeToken: ZCASH,
    displayColor: '#D89E0D',
    doesBackendSocketSupport: false,
    canImportTokens: false,
    shouldShowScamWarningIfNotEnoughGas: false,
    feeCheckAddress: 't1HxutHFt2Sejz7fs92wFVAbsFM7NDjsBG6',
    usdtSlug: {
      mainnet: undefined,
      testnet: undefined,
    },
    doesSupportPushNotifications: false,
    defaultEnabledSlugs: {
      mainnet: [ZCASH.slug],
      testnet: [ZCASH.slug],
    },
    crosschainSwapSlugs: [ZCASH.slug],
    tokenInfo: [ZCASH],
    explorers: [{
      id: 'blockchair',
      name: 'Blockchair',
      baseUrl: {
        mainnet: 'https://blockchair.com/zcash/',
        testnet: 'https://blockchair.com/zcash/testnet/',
      },
      address: '{base}address/{address}',
      token: '{base}address/{address}',
      transaction: '{base}transaction/{hash}',
      doConvertHashFromBase64: false,
    }],
    marketplaces: [],
    isNetWorthSupported: false,
  },
};

export const VIEW_ACCOUNT_EVM_PARAM = 'evm';

if (DEBUG) {
  const configKeys = new Set(Object.keys(CHAIN_CONFIG));
  const supportedSet = new Set(CHAIN_ORDER);
  const missing = [...configKeys].filter((k) => !supportedSet.has(k as ApiChain));
  if (missing.length) {
    throw new Error(`SUPPORTED_CHAINS is missing chains from CHAIN_CONFIG: ${missing.join(', ')}`);
  }

  const displaySet = new Set(CHAIN_DISPLAY_ORDER);
  const displayMissing = [...supportedSet].filter((k) => !displaySet.has(k));
  if (displayMissing.length || displaySet.size !== supportedSet.size) {
    throw new Error(`CHAIN_DISPLAY_ORDER must contain the same chains as CHAIN_ORDER: ${displayMissing.join(', ')}`);
  }
}

export function getChainConfig(chain: ApiChain): ChainConfig {
  // The `ApiChain` parameter type is statically narrow, but persisted storage can hold chain
  // keys from older schemas, so guard here so callers see the chain name instead of an opaque
  // `undefined.<prop>` further down the stack
  const config = CHAIN_CONFIG[chain];
  if (!config) {
    throw new Error(`Unsupported chain "${chain}" — not present in CHAIN_CONFIG`);
  }
  return config;
}

export function findChainConfig(chain: string | undefined): ChainConfig | undefined {
  return chain ? CHAIN_CONFIG[chain as ApiChain] : undefined;
}

export function getAvailableExplorers(chain: ApiChain): ExplorerConfig[] {
  return getChainConfig(chain).explorers;
}

export function getAvailableMarketplaces(chain: ApiChain): MarketplaceConfig[] {
  return getChainConfig(chain).marketplaces;
}

export function getExplorer(chain: ApiChain, explorerId?: string): ExplorerConfig {
  const explorers = getAvailableExplorers(chain);

  if (explorerId) {
    const explorer = explorers.find((e) => e.id === explorerId);
    if (explorer) return explorer;
  }

  return explorers[0];
}

export function getMarketplace(chain: ApiChain, id?: string): MarketplaceConfig {
  const marketplaces = getAvailableMarketplaces(chain);

  if (id) {
    const marketplace = marketplaces.find((e) => e.id === id);
    if (marketplace) return marketplace;
  }

  return marketplaces[0];
}

export function getChainTitle(chain: ApiChain) {
  return getChainConfig(chain).title;
}

export function getIsSupportedChain(chain?: string): chain is ApiChain {
  return chain !== undefined && Object.prototype.hasOwnProperty.call(CHAIN_CONFIG, chain);
}

export function getSupportedChains() {
  return CHAIN_ORDER;
}

/** All supported chains in the UI display order (see `CHAIN_DISPLAY_ORDER`) */
export function getDisplayOrderedChains() {
  return CHAIN_DISPLAY_ORDER;
}

export function getChainsByStandard(chainStandard: ApiChain) {
  return getSupportedChains().filter((chain) => getChainConfig(chain).chainStandard === chainStandard);
}

export function getEvmChains() {
  return getChainsByStandard('ethereum');
}

export function isUtxoChain(chain: ApiChain): chain is UTXOChain {
  return chain in UTXO_COIN_TYPES;
}

/** Returns the chains supported by the given account in the proper order for showing in the UI */
export function getOrderedAccountChains(byChain: Partial<Record<ApiChain, unknown>>) {
  return getDisplayOrderedChains().filter((chain) => chain in byChain);
}

export function getChainsSupportingLedger(): ApiChain[] {
  return getSupportedChains()
    .filter((chain) => CHAIN_CONFIG[chain].isLedgerSupported);
}

export const getChainsSupportingNft = /* #__PURE__ */ withCache((): ReadonlySet<ApiChain> => {
  return new Set(
    getSupportedChains()
      .filter((chain) => CHAIN_CONFIG[chain].isNftSupported),
  );
});

export const getTrustedUsdtSlugs = /* #__PURE__ */ withCache((): ReadonlySet<string> => {
  return new Set(
    Object.values(CHAIN_CONFIG).flatMap(({ usdtSlug }) => {
      return compact([
        usdtSlug.mainnet,
        usdtSlug.testnet,
      ]);
    }),
  );
});

/** The USDT and USDC variants of every chain, which the Buy / Sell asset picker groups as stablecoins */
export const getStablecoinSlugs = /* #__PURE__ */ withCache((): ReadonlySet<string> => {
  return new Set(
    Object.values(CHAIN_CONFIG).flatMap(({ usdtSlug, usdcSlug }) => {
      return compact([
        usdtSlug.mainnet,
        usdtSlug.testnet,
        usdcSlug?.mainnet,
        usdcSlug?.testnet,
      ]);
    }),
  );
});

export const getDefaultEnabledSlugs = /* #__PURE__ */ withCache((network: ApiNetwork): ReadonlySet<string> => {
  // The TON-forward Gram brand defaults to TON tokens even though it supports every chain, matching Air
  // (`ApiToken.defaultSlugs`). It also spares the legacy wallet.ton.org accounts, whose TON-native mnemonic cannot
  // derive foreign addresses, zero-balance rows they can never use: `updateBalances` (`global/reducers/misc.ts`)
  // seeds every default slug and empty wallets render them all.
  const chainConfigs = IS_GRAM_WALLET ? [CHAIN_CONFIG.ton] : Object.values(CHAIN_CONFIG);

  return new Set(
    chainConfigs.flatMap((chainConfig) => chainConfig.defaultEnabledSlugs[network]),
  );
});

/**
 * The chains a wallet that holds nothing shows: there are no balances to sort or filter by, so it offers everything
 * it supports to receive the first funds in. Keyed on the build like `getDefaultEnabledSlugs` - the Gram brand is
 * TON-forward, so its empty wallets list TON alone.
 */
export const getAllSupportedVisibleChains = /* #__PURE__ */ withCache((): ReadonlySet<ApiChain> => {
  return new Set(IS_GRAM_WALLET ? [TONCOIN.chain] : getSupportedChains());
});

export const getSlugsSupportingCexSwap = /* #__PURE__ */ withCache((): ReadonlySet<string> => {
  return new Set(
    Object.values(CHAIN_CONFIG)
      .flatMap((chainConfig) => chainConfig.crosschainSwapSlugs),
  );
});

/** Returns the tokens from all the chains to fill the token cache until it's loaded from the backend */
export const getTokenInfo = /* #__PURE__ */ withCache((): Readonly<Record<string, ApiTokenWithPrice>> => {
  const commonToken = {
    isFromBackend: true,
    priceUsd: 0,
    percentChange24h: 0,
  };

  const allTokens = Object.values(CHAIN_CONFIG).flatMap((chainConfig) => {
    return chainConfig.tokenInfo.map((token) => ({ ...commonToken, ...token }));
  });

  return buildCollectionByKey(allTokens, 'slug');
});
